import Modal from '@components/v2/ui/Modal/Modal';
import { Alert, Box } from '@mui/material';
import { Grid } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import Container from '@mui/material/Container';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useEffect, useState } from 'react';
import AutocompleteMuiAdapter from '../../components/adaptersMui/AutocompleteMuiAdapter/AutocompleteMuiAdapter';
import LangEnum from '../../lang/LangEnum';
import snackbarStore from '../../stores/Snackbar.store';
import teceoInputConfig from '../../utils/TeceoInputConfig';
import { useApplicationContext } from '../context/ApplicationContext/ApplicationContext';
import { CustomerSynchronizedStatus } from '../customer/types/customer.enum';
import { SalesRepresentative } from '../sales-representative/sales-representative.types';
import { useTranslation } from '../translation/TranslationContext';
import { myNetworkConnectionsRepository } from './connections/MyNetworkConnections.repository';
import { Connection, Invite } from './myNetwork.types';
import { myNetworkPageRepository } from './myNetworkPage.repository';

type EditSalesRepModalProps = {
  open: boolean;
  onClose: (success?: boolean) => void;
  item: Connection | null | Invite;
  isConnection?: boolean;
  disableSuccessMessage?: boolean;
  disableReloadAfterSuccess?: boolean;
  reload: () => void;
  submitLabel?: string;
};

function sleep(delay = 0) {
  return new Promise((resolve) => {
    setTimeout(resolve, delay);
  });
}

const EditSalesRepModal = ({
  open,
  onClose,
  isConnection,
  item,
  disableSuccessMessage,
  disableReloadAfterSuccess,
  reload,
  submitLabel,
}: EditSalesRepModalProps) => {
  const { state } = useApplicationContext();
  const { handleMessage } = snackbarStore;
  const [salesReps, setSalesRep] = useState<SalesRepresentative[]>([]);
  const [options, setOptions] = useState<SalesRepresentative[]>([]);
  const [loading, setIsLoading] = useState(true);
  const [inputText, setInputText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { translate } = useTranslation();

  const handleClose = (success: boolean) => {
    setOptions([]);
    setSalesRep([]);
    setInputText('');
    setIsLoading(false);
    setIsSubmitting(false);
    onClose(success);
  };

  const handleSubmit = async () => {
    if (!item) {
      handleMessage({
        type: 'error',
        message: translate(LangEnum.MY_NETWORK_EDIT_SALES_REP_SUBMISSION_ERROR),
      });
      return;
    }
    setIsSubmitting(true);
    let response;
    if (isConnection) {
      response = await myNetworkConnectionsRepository(item?.id).updateSalesReps(
        salesReps.map((salesRep) => salesRep.id),
      );
    } else {
      response = await myNetworkPageRepository().updateSalesReps(
        item?.id,
        salesReps.map((salesRep) => salesRep.id),
      );
    }
    setIsSubmitting(false);
    if (response.ok) {
      if (!disableSuccessMessage) {
        handleMessage({
          type: 'success',
          message: translate(LangEnum.SUCCESS_SAVE_MESSAGE),
        });
      }
      if (!disableReloadAfterSuccess) {
        reload();
      }
      handleClose(true);
      return;
    }
    handleMessage({
      type: 'error',
      message: translate(LangEnum.MY_NETWORK_EDIT_SALES_REP_CONNECTION_ERROR),
    });
  };

  const loadOptions = async () => {
    if (item) {
      setIsLoading(true);
      const response = await myNetworkConnectionsRepository(item?.id).getSalesReps(inputText);
      if (response.ok) {
        setOptions(response.data.data);
        setIsLoading(false);
        return;
      }
      setIsLoading(false);
      handleMessage({
        message: translate(LangEnum.MY_NETWORK_EDIT_SALES_REP_LOADING_SELLERS_ERROR),
        type: 'error',
      });
    }
  };

  useEffect(() => {
    (async () => {
      loadOptions();
      await sleep(1e3);
    })();
  }, [inputText, state.user, item]);

  useEffect(() => {
    if (item?.salesRepresentatives) {
      setSalesRep(item?.salesRepresentatives);
    }
  }, [item]);

  if (!item) {
    return null;
  }

  const readMode =
    item?.customerSynchronizedStatus &&
    [CustomerSynchronizedStatus.PROCESSING, CustomerSynchronizedStatus.SUCCESS].includes(
      item.customerSynchronizedStatus,
    );

  return (
    <Modal
      isOpen={open}
      size="standard"
      onClose={() => handleClose(false)}
      onExited={() => setSalesRep([])}
      header={{
        title: translate(LangEnum.MY_NETWORK_EDIT_SALES_REP_TITLE, {
          storeName: item?.store?.name,
        }),
      }}
      footer={{
        primaryAction: {
          label: submitLabel || LangEnum.GLOBAL_SAVE,
          isDisabled: (isConnection && readMode) || isSubmitting,
          onClick: handleSubmit,
        },
        secondaryAction: {
          label: LangEnum.GLOBAL_CANCEL_LABEL,
          onClick: () => handleClose(false),
        },
      }}
    >
      <Container maxWidth="sm" disableGutters>
        <Box>
          <Grid container spacing={2}>
            {readMode && (
              <Grid size={12}>
                <Alert severity="info">
                  {translate(LangEnum.CUSTOMER_INTEGRATED_ALERT_INFO_TEXT)}
                </Alert>
              </Grid>
            )}
            <Grid size={12}>
              <Typography align="center" color="textSecondary">
                {translate(LangEnum.MY_NETWORK_EDIT_SALES_REP_TEXT)}
              </Typography>
            </Grid>
            <Grid size={12}>
              <AutocompleteMuiAdapter
                multiple
                disabled={readMode}
                options={options}
                disableCloseOnSelect
                loadingText={translate(LangEnum.GLOBAL_LOADING)}
                noOptionsText={translate(LangEnum.GLOBAL_NOT_FOUND)}
                getOptionLabel={({ name, code }: SalesRepresentative) => `${code} - ${name}`}
                isOptionEqualToValue={(option, value) => option.code === value.code}
                value={salesReps}
                onChange={(_, value) => setSalesRep(value)}
                onInputChange={(_, value) => setInputText(value)}
                loading={loading}
                renderOption={({ name, code }: SalesRepresentative) => `${code} - ${name}`}
                renderInput={(params: any) => (
                  <TextField
                    {...params}
                    {...teceoInputConfig}
                    fullWidth
                    variant="outlined"
                    label={translate(LangEnum.MY_NETWORK_EDIT_SALES_REP_NAME)}
                    placeholder={translate(LangEnum.MY_NETWORK_EDIT_SALES_REP_NAME_PLACEHOLDER)}
                    slotProps={{
                      input: {
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loading && <CircularProgress color="inherit" size={20} />}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      },
                    }}
                  />
                )}
              />
            </Grid>
          </Grid>
        </Box>
      </Container>
    </Modal>
  );
};

export default EditSalesRepModal;
